/**
 * Assistant Store
 *
 * Org-scoped Assistant chat, running on the Unified Agentic Pipeline
 * (orchestrator) transport — the same engine that powers the section editor and
 * tender chat, entered here through the ORG-scope chat lane. Conversations are
 * agent THREADS within a single per-user org chat session. Streaming, citations,
 * and the honest retrieval-status line all ride the agent StreamEvent union.
 *
 * The assistant differs from the tender lane in three ways:
 *   1. Org scope (no bid) — plus an optional per-turn `grantBidId` (from the
 *      Sources picker, wired in Task 3) sent as `scope.bidId`.
 *   2. A `ChatContext` (selected library/bid docs, Fact-Bank toggle) held as UI
 *      state and mapped at send time to `selectedDocumentIds` + `factBankOnly`.
 *   3. Quick-write artifacts: a turn may stream a standalone document into the
 *      side panel (`artifact_started/chunk/completed`), which the user can then
 *      quote back as per-turn `selection` context.
 *
 * This store deliberately does NOT import from tender-chat-store: the shared
 * patterns (citation accumulation, positional quote merge, replay resume, the
 * honest-status line) are ported here / pulled from shared libs so neither store
 * depends on the other.
 */

import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type {} from '@redux-devtools/extension'
import {
  getOrCreateOrgChatSession,
  createAgentThread,
  activateAgentThread,
  updateAgentThread,
  deleteAgentThread,
  type AgentThread,
  type ChatLaneSessionResponse,
  type BidChatTurnHistoryEntry,
} from '@/lib/api'
import {
  sendAgentTurn,
  replayTurnEvents,
  cancelAgentTurn,
  type StreamEvent,
} from '@/lib/agent-client'
import {
  reduceActivityPhases,
  initialPhaseState,
  optimisticPhaseState,
  type PhaseState,
} from '@/lib/activity-phases'
import { deriveRetrievalDetail } from '@/lib/retrieval-status'
import {
  appendMemorySavedLine,
  dropOrSettleReply,
  parseMemorySavedLine,
  undoMemorySavedLine,
  type MemorySavedLine,
} from '@/lib/memory-saved'
import { usePreviewStore } from '@/stores/preview-store'

// ===================
// Types
// ===================

/**
 * Highlight data for citation rendering (V1: PDF bounding boxes, V2: text-search).
 * Owned here because both the tender store and citation rendering import it.
 */
export type SourceHighlightData =
  | {
      v: 1
      itemSpans: Array<{
        pageNumber: number
        startItemIndex: number
        endItemIndex: number
      }>
      pages: Array<{
        pageNumber: number
        rects: Array<{
          x1: number
          y1: number
          x2: number
          y2: number
        }>
      }>
    }
  | {
      v: 2
      chunkText: string
      sectionHeader: string | null
    }

export interface ChatMessage {
  id: string
  sessionId: string
  role: 'user' | 'assistant' | 'system'
  content: string
  createdAt: string
  sources?: Array<{
    documentId: string
    documentName: string
    pageNumber: number | null
    pageNumbers?: number[]
    snippet?: string
    /** Verified verbatim quote for the cited claim (quote-first highlighting). Absent on legacy rows. */
    quote?: string
    highlightData?: SourceHighlightData | null
    confidenceBand?: 'high' | 'medium' | 'low'
  }>
  librarySources?: Array<{
    documentId: string
    documentName: string
    pageNumber: number | null
    snippet?: string
    /** Verified verbatim quote for the cited claim (quote-first highlighting). Absent on legacy rows. */
    quote?: string
    highlightData?: SourceHighlightData | null
  }>
  attachments?: Array<{
    attachmentId: string
    filename: string
    mimeType: string
    size?: number
  }>
  isStreaming?: boolean
  isPartial?: boolean // Indicates incomplete message due to connection error
  retrievalStatus?: 'ok' | 'partial' | 'empty' | 'weak'
  /**
   * Pre-derived honest-status line shown quietly under a completed assistant
   * message (§2.2). Set from the live stream and rehydrated from history.
   */
  retrievalDetail?: string
  /**
   * Quick-write document produced by this turn. Carried on the assistant
   * message so the chip can re-open it from history without another fetch.
   */
  artifact?: {
    turnId: string
    title: string
    content: string
    truncated?: boolean
  }
  /**
   * Passage the user had selected in the artifact panel when they sent this
   * message — rendered as a quote above the user's bubble.
   */
  quotedText?: string
  /**
   * Bid memory the agent saved during this reply, one "Saved to memory · Undo"
   * line each, in save order. Live only: a reply reloaded from history does not
   * carry it. Shared with Tender Chat, which uses this message type too.
   */
  memorySaved?: MemorySavedLine[]
}

/**
 * The document currently held by the artifact panel: streaming in from the
 * live turn, or re-opened from a past message. `groundedSourceCount` is the
 * number of sources the turn fetched. The panel shows it only while the
 * document streams; once complete, the grounding line counts the sources the
 * document actually cites (see countCitedSources).
 *
 * `sources` / `librarySources` are the producing turn's citation lists — the
 * same arrays its assistant message carries — so the panel can resolve the
 * document's `[N]` / `[LN]` markers into clickable citations. Absent while the
 * document streams (markers render as pending) and when the turn had none.
 */
export interface AssistantArtifact {
  turnId: string
  title: string
  content: string
  status: 'streaming' | 'complete'
  truncated: boolean
  groundedSourceCount: number
  sources?: ChatMessage['sources']
  librarySources?: ChatMessage['librarySources']
}

/** A document the user added to the Assistant's Sources (library folder tree or a bid's docs). */
export interface SelectedDocument {
  id: string
  name: string
  type: 'library' | 'bid'
  bidId?: string
  folderId?: string
}

/**
 * What the Assistant should search for the next turn. Held as UI state and
 * mapped at send time onto the turn request. `searchMode` is retained but
 * unwired (deleted stage 4).
 */
export interface ChatContext {
  selectedDocuments?: SelectedDocument[]
  searchMode?: 'documents' | 'web' | 'both'
  allLibrary?: boolean // When true, search all library documents
  factBankOnly?: boolean // Layer 1: scope the answer to the Fact Bank
}

type CitationSource = NonNullable<ChatMessage['sources']>[number]
type LibraryCitationSource = NonNullable<ChatMessage['librarySources']>[number]

interface AssistantChatState {
  // Session / threads
  sessionId: string | null
  activeThreadId: string | null
  threads: AgentThread[]
  /** threadId → first-user-message preview, for the history sidebar labels. */
  threadPreviews: Record<string, string>
  messages: ChatMessage[]

  // Loading / streaming
  isLoading: boolean
  isStreaming: boolean
  streamingContent: string
  error: string | null

  // In-flight turn (for cancel + replay resume)
  currentTurnId: string | null

  /** Raw last stream event — a clean seam for the activity-phase reducer. */
  lastStreamEvent: StreamEvent | null

  /**
   * §2.7 activity phases for the in-flight/last turn. Folded from
   * `lastStreamEvent` by the store-level subscription below. The Assistant does
   * NOT mount `<ActivityPhases>` this stage (stage 4 owns "phases everywhere"),
   * but the seam is kept so nothing has to change then.
   */
  activityPhases: PhaseState

  // Quick-write artifact panel
  /** The document in the panel — streaming, complete, or re-opened from history. */
  artifact: AssistantArtifact | null
  artifactPanelOpen: boolean
  /** Passage the user highlighted in the panel, sent as context on the next turn. */
  panelSelection: { text: string } | null

  /**
   * Optional per-turn bid grant (Task 3 wires the Sources UI to set it). When
   * set, sent as `scope.bidId` so the turn may also search that bid's tender
   * documents. Default null = library-only.
   */
  grantBidId: string | null

  // What the AI knows about the current view
  context: ChatContext

  // Artifact panel actions
  /** Re-open a past turn's document from its message chip. No-op without one. */
  openArtifactFromMessage: (message: ChatMessage) => void
  closeArtifactPanel: () => void
  setPanelSelection: (sel: { text: string } | null) => void

  // Context / grant actions
  setContext: (context: ChatContext) => void
  setGrantBidId: (bidId: string | null) => void

  // Session / thread actions
  reloadActiveThread: (isCurrent?: () => boolean) => Promise<void>
  selectThread: (threadId: string, isCurrent?: () => boolean) => Promise<void>
  startNewChat: (isCurrent?: () => boolean) => Promise<void>
  /** Rethrows on failure so the rename dialog can stay open. */
  renameThread: (threadId: string, name: string) => Promise<void>
  togglePinThread: (threadId: string) => Promise<void>
  /** Rethrows on failure so callers can observe it; `error` carries the message. */
  deleteThread: (threadId: string) => Promise<void>
  /** Resolves `true` once the turn was dispatched; `false` when the session
   * wasn't ready, so the composer can keep the user's draft. */
  sendMessage: (
    content: string,
    attachments?: Array<{ attachmentId: string; s3Key: string; filename: string; mimeType: string; size: number }>
  ) => Promise<boolean>
  cancelStream: () => void
  clearError: () => void
  /**
   * Undo a "Saved to memory" line: deletes the item. Never throws; the line's
   * status carries the outcome ("Already removed" when someone got there first).
   */
  undoMemorySaved: (messageId: string, memoryId: string) => Promise<void>
}

// ===================
// Module-level abort controller for stream cancellation
// ===================

let streamAbortController: AbortController | null = null

// ===================
// Shared state fragments
// ===================

/**
 * Empty the artifact surface. Spread into every transition that changes which
 * thread is on screen (select / new / delete-the-active-one): the panel is
 * thread-scoped, so without this it carries thread A's document into thread B,
 * and a document caught mid-write by the switch would sit at status
 * 'streaming' forever — no further `artifact_chunk` can arrive once that
 * turn's stream is aborted.
 *
 * Nothing is lost: a completed document stays on its assistant message and is
 * re-openable from the history chip, which is the designed re-find path.
 */
const clearedArtifactSurface = {
  artifact: null,
  artifactPanelOpen: false,
  panelSelection: null,
} satisfies Partial<AssistantChatState>

// ===================
// Store Implementation
// ===================

export const useAssistantStore = create<AssistantChatState>()(
  devtools(
    (set, get) => ({
      // Initial state
      sessionId: null,
      activeThreadId: null,
      threads: [],
      threadPreviews: {},
      messages: [],
      isLoading: false,
      isStreaming: false,
      streamingContent: '',
      error: null,
      currentTurnId: null,
      lastStreamEvent: null,
      activityPhases: initialPhaseState(),
      artifact: null,
      artifactPanelOpen: false,
      panelSelection: null,
      grantBidId: null,
      context: {}, // No default context — searches all library when empty

      // ===================
      // Artifact panel
      // ===================

      openArtifactFromMessage: (message: ChatMessage) => {
        const artifact = message.artifact
        if (!artifact) return
        usePreviewStore.getState().closePreview()
        set({
          artifact: {
            turnId: artifact.turnId,
            title: artifact.title,
            content: artifact.content,
            status: 'complete',
            truncated: artifact.truncated === true,
            groundedSourceCount:
              (message.librarySources?.length ?? 0) + (message.sources?.length ?? 0),
            ...artifactSourcesFrom(message.sources ?? [], message.librarySources ?? []),
          },
          artifactPanelOpen: true,
          panelSelection: null,
        })
      },

      closeArtifactPanel: () => set({ artifactPanelOpen: false, panelSelection: null }),

      setPanelSelection: (sel: { text: string } | null) => set({ panelSelection: sel }),

      // ===================
      // Context / grant
      // ===================

      setContext: (context: ChatContext) => {
        set({ context })
      },

      setGrantBidId: (bidId: string | null) => {
        set({ grantBidId: bidId })
      },

      // ===================
      // Session lifecycle
      // ===================

      reloadActiveThread: async (isCurrent = () => true) => {
        if (!isCurrent()) return
        try {
          set({ isLoading: true, error: null })
          const response = await getOrCreateOrgChatSession()
          if (isCurrent()) applySessionResponse(set, response)
        } catch (error) {
          if (!isCurrent()) return
          set({
            error: error instanceof Error ? error.message : 'Failed to load the assistant',
            isLoading: false,
          })
        }
      },

      selectThread: async (threadId: string, isCurrent = () => true) => {
        if (!isCurrent()) return
        const { sessionId, activeThreadId } = get()
        if (!sessionId || threadId === activeThreadId) return

        // Abort any in-flight stream before switching threads.
        streamAbortController?.abort()
        streamAbortController = null

        // Clear any streaming placeholders before switching threads.
        set((state) => ({
          isStreaming: false,
          streamingContent: '',
          activityPhases: initialPhaseState(),
          messages: state.messages.filter((m) => !m.isStreaming),
          ...clearedArtifactSurface,
        }))

        try {
          set({ isLoading: true, error: null })
          await activateAgentThread(sessionId, threadId)
          if (!isCurrent()) return
          // Re-fetch the session: the backend returns the now-active thread's
          // history (activate itself returns no turns).
          const response = await getOrCreateOrgChatSession()
          if (isCurrent()) applySessionResponse(set, response)
        } catch (error) {
          if (!isCurrent()) return
          set({
            error: error instanceof Error ? error.message : 'Failed to open conversation',
            isLoading: false,
          })
        }
      },

      startNewChat: async (isCurrent = () => true) => {
        if (!isCurrent()) return
        const { sessionId } = get()

        // Abort any in-flight stream before clearing streaming state.
        streamAbortController?.abort()
        streamAbortController = null

        if (!sessionId) {
          set({
            messages: [],
            streamingContent: '',
            isStreaming: false,
            error: null,
            ...clearedArtifactSurface,
          })
          return
        }

        set((state) => ({
          isLoading: true,
          error: null,
          isStreaming: false,
          streamingContent: '',
          messages: state.messages.filter((m) => !m.isStreaming).length === 0 ? state.messages : [],
          ...clearedArtifactSurface,
        }))

        try {
          const response = await createAgentThread(sessionId, 'fresh')
          if (!isCurrent()) return
          const newThreadId = response.activeThreadId ?? response.thread.id
          set((state) => ({
            activeThreadId: newThreadId,
            threads: [response.thread, ...state.threads.filter((t) => t.id !== response.thread.id)],
            messages: [],
            isLoading: false,
          }))
        } catch (error) {
          if (!isCurrent()) return
          set({
            error: error instanceof Error ? error.message : 'Failed to start a new chat',
            isLoading: false,
          })
        }
      },

      // ===================
      // Thread management (rename / pin / delete)
      // ===================

      renameThread: async (threadId: string, name: string) => {
        const { sessionId, threads } = get()
        if (!sessionId) return

        const previousThreads = threads
        set({ threads: threads.map((t) => (t.id === threadId ? { ...t, name } : t)) })

        try {
          await updateAgentThread(sessionId, threadId, { name })
        } catch (error) {
          set({
            threads: previousThreads,
            error: error instanceof Error ? error.message : 'Failed to rename conversation',
          })
          throw error
        }
      },

      togglePinThread: async (threadId: string) => {
        const { sessionId, threads } = get()
        if (!sessionId) return

        const thread = threads.find((t) => t.id === threadId)
        if (!thread) return

        const previousThreads = threads
        const isPinned = !thread.isPinned
        set({ threads: threads.map((t) => (t.id === threadId ? { ...t, isPinned } : t)) })

        try {
          await updateAgentThread(sessionId, threadId, { isPinned })
        } catch (error) {
          set({
            threads: previousThreads,
            error: error instanceof Error ? error.message : 'Failed to update conversation',
          })
        }
      },

      deleteThread: async (threadId: string) => {
        const { sessionId, activeThreadId } = get()
        if (!sessionId) return

        const wasActive = threadId === activeThreadId

        if (wasActive) {
          // Same guard as selectThread: never leave a stream writing into a
          // thread that no longer exists.
          streamAbortController?.abort()
          streamAbortController = null
        }

        try {
          // The backend owns the next active thread (most recent remaining, or a
          // freshly seeded one) — adopt what it returns, never compute it here.
          const response = await deleteAgentThread(sessionId, threadId)
          set({
            threads: response.threads,
            activeThreadId: response.activeThreadId,
            // Only an active-thread delete changes what is on screen. The docked
            // document always belongs to the active thread, so deleting some
            // other conversation from the sidebar must leave it open.
            ...(wasActive
              ? {
                  messages: [],
                  isStreaming: false,
                  streamingContent: '',
                  activityPhases: initialPhaseState(),
                  ...clearedArtifactSurface,
                }
              : {}),
          })

          if (wasActive) {
            // The replacement thread may carry history — load it the same way
            // selectThread does.
            const sessionResponse = await getOrCreateOrgChatSession()
            applySessionResponse(set, sessionResponse)
          }
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to delete conversation',
          })
          throw error
        }
      },

      // ===================
      // Send / streaming
      // ===================

      sendMessage: async (content, attachments) => {
        // Ensure a session + active thread exist (mount primes them; be defensive).
        let { sessionId, activeThreadId } = get()
        if (!sessionId || !activeThreadId) {
          await get().reloadActiveThread()
          sessionId = get().sessionId
          activeThreadId = get().activeThreadId
          if (!sessionId || !activeThreadId) {
            set({ error: 'The assistant is not ready yet. Please try again.' })
            return false
          }
        }

        // Map the ChatContext onto turn-request fields. Org scope; the optional
        // per-turn grant widens it to a bid.
        const { context, grantBidId, panelSelection } = get()
        const scope = { kind: 'org' as const, ...(grantBidId ? { bidId: grantBidId } : {}) }
        const selectedDocumentIds = (context.selectedDocuments ?? []).map((doc) => doc.id)
        const factBankOnly = context.factBankOnly === true

        // Optimistic user message
        const userMessage: ChatMessage = {
          id: crypto.randomUUID(),
          sessionId,
          role: 'user',
          content,
          createdAt: new Date().toISOString(),
          ...(attachments && attachments.length > 0 && {
            attachments: attachments.map((a) => ({
              attachmentId: a.attachmentId,
              filename: a.filename,
              mimeType: a.mimeType,
              size: a.size,
            })),
          }),
          ...(panelSelection ? { quotedText: panelSelection.text } : {}),
        }

        const streamingId = `streaming-${crypto.randomUUID()}`
        const streamingPlaceholder: ChatMessage = {
          id: streamingId,
          sessionId,
          role: 'assistant',
          content: '',
          createdAt: new Date().toISOString(),
          isStreaming: true,
        }

        set((state) => ({
          messages: [...state.messages, userMessage, streamingPlaceholder],
          isStreaming: true,
          streamingContent: '',
          error: null,
          currentTurnId: null,
          // The quote is now on the user's bubble — the panel highlight is spent.
          panelSelection: null,
          activityPhases: optimisticPhaseState(),
          threadPreviews: activeThreadId && !state.threadPreviews[activeThreadId]
            ? { ...state.threadPreviews, [activeThreadId]: previewFromContent(content) }
            : state.threadPreviews,
        }))

        // Per-turn accumulators (ported from the panel; local, not shared).
        const turnCitations: CitationSource[] = []
        const turnLibraryCitations: LibraryCitationSource[] = []
        let retrievalStatus: 'ok' | 'partial' | 'empty' | 'weak' | undefined
        let fallbackUsed = false
        let lastSeq = 0
        let sawTerminal = false

        // User-initiated retries rotate the key; transport retries (replay) reuse it.
        const idempotencyKey = crypto.randomUUID()

        /**
         * The artifact to stamp on THIS turn's assistant message. The panel keeps
         * the last document across later turns, so the turn id must match or a
         * plain follow-up would inherit the previous turn's chip.
         */
        const artifactForThisTurn = (): ChatMessage['artifact'] => {
          const a = get().artifact
          if (!a || a.status !== 'complete') return undefined
          if (a.turnId !== get().currentTurnId) return undefined
          return {
            turnId: a.turnId,
            title: a.title,
            content: a.content,
            ...(a.truncated ? { truncated: true } : {}),
          }
        }

        const handleEvent = (event: StreamEvent) => {
          // Seq de-dup so replay after a drop never double-applies chunks.
          if (event.seq > 0) {
            if (event.seq <= lastSeq) return
            lastSeq = event.seq
          }
          if (event.turnId && get().currentTurnId !== event.turnId) {
            set({ currentTurnId: event.turnId })
          }
          // Activity-phase seam: expose the raw event to subscribers.
          set({ lastStreamEvent: event })

          switch (event.eventType) {
            case 'assistant_message_chunk': {
              const chunk =
                (event.data['text'] as string | undefined) ??
                (event.data['chunk'] as string | undefined) ??
                ''
              if (!chunk) break
              set((state) => ({
                messages: state.messages.map((m) =>
                  m.id === streamingId ? { ...m, content: m.content + chunk, isStreaming: true } : m
                ),
                streamingContent: state.streamingContent + chunk,
              }))
              break
            }

            case 'evidence_search_completed': {
              mergeCitations(turnCitations, event.data['citations'])
              mergeCitations(turnLibraryCitations, event.data['libraryCitations'])
              const status = event.data['retrievalStatus']
              if (status === 'ok' || status === 'partial' || status === 'empty' || status === 'weak') {
                retrievalStatus = status
              }
              if (event.data['fallbackUsed'] === true) fallbackUsed = true
              break
            }

            case 'citation_quotes': {
              mergeQuotes(turnCitations, turnLibraryCitations, event.data['quotes'])
              break
            }

            // ─── Quick-write artifact (streams into the side panel) ───

            case 'artifact_started': {
              const title = (event.data['title'] as string | undefined) ?? 'Document'
              // The panel takes the document preview's slot — evict it.
              usePreviewStore.getState().closePreview()
              set({
                artifact: {
                  turnId: event.turnId,
                  title,
                  content: '',
                  status: 'streaming',
                  truncated: false,
                  groundedSourceCount: turnCitations.length + turnLibraryCitations.length,
                },
                artifactPanelOpen: true,
                panelSelection: null,
              })
              break
            }

            case 'artifact_chunk': {
              const delta = (event.data['delta'] as string | undefined) ?? ''
              if (!delta) break
              set((state) =>
                state.artifact
                  ? { artifact: { ...state.artifact, content: state.artifact.content + delta } }
                  : {}
              )
              break
            }

            case 'artifact_completed': {
              const title = (event.data['title'] as string | undefined) ?? 'Document'
              const content = (event.data['content'] as string | undefined) ?? ''
              const truncated = event.data['truncated'] === true
              // Canonical replace: the server's copy wins over the accumulated chunks.
              // Sources are snapshotted now so the markers resolve as soon as the
              // document lands; turn completion refreshes them (late quotes).
              set({
                artifact: {
                  turnId: event.turnId,
                  title,
                  content,
                  status: 'complete',
                  truncated,
                  groundedSourceCount: turnCitations.length + turnLibraryCitations.length,
                  ...artifactSourcesFrom(turnCitations, turnLibraryCitations),
                },
                artifactPanelOpen: true,
              })
              break
            }

            case 'memory_saved': {
              // The agent saved something to bid memory: record it on this
              // turn's reply so the bubble shows "Saved to memory · Undo".
              const line = parseMemorySavedLine(event.data)
              if (!line) break
              set((state) => ({ messages: appendMemorySavedLine(state.messages, streamingId, line) }))
              break
            }

            case 'turn_completed': {
              sawTerminal = true
              finalizeAssistantMessage(set, streamingId, {
                citations: turnCitations,
                libraryCitations: turnLibraryCitations,
                retrievalStatus,
                fallbackUsed,
                artifact: artifactForThisTurn(),
              })
              break
            }

            case 'turn_error': {
              sawTerminal = true
              const rawMessage = event.data['message']
              const message = typeof rawMessage === 'string' && rawMessage.length > 0
                ? rawMessage
                : 'The assistant ran into a problem. Please try again.'
              set((state) => ({
                messages: dropOrSettleReply(state.messages, (m) => m.id === streamingId),
                isStreaming: false,
                streamingContent: '',
                currentTurnId: null,
                error: message,
                ...settleStreamingArtifact(state),
              }))
              break
            }

            // heartbeat and all editor-only events: no-op for chat.
            default:
              break
          }
        }

        const myController = new AbortController()
        streamAbortController = myController
        try {
          await sendAgentTurn(
            {
              sessionId,
              threadId: activeThreadId,
              scope,
              message: content,
              idempotencyKey,
              ...(selectedDocumentIds.length > 0 ? { selectedDocumentIds } : {}),
              ...(factBankOnly ? { factBankOnly: true } : {}),
              ...(attachments && attachments.length > 0 ? { attachments } : {}),
              ...(panelSelection ? { selection: panelSelection } : {}),
            },
            handleEvent,
            myController.signal
          )

          // Stream ended cleanly but no terminal event arrived — finalize what we have.
          if (!sawTerminal && get().isStreaming) {
            finalizeAssistantMessage(set, streamingId, {
              citations: turnCitations,
              libraryCitations: turnLibraryCitations,
              retrievalStatus,
              fallbackUsed,
              artifact: artifactForThisTurn(),
            })
          }
        } catch (error) {
          const code = errorCode(error)

          if (code === 'CANCELLED') {
            // cancelStream already tidied state.
            return true
          }

          // Transport dropped mid-stream — try to resume from where we left off.
          // Pass the same controller's signal so Stop during reconnect aborts
          // the replay fetch too.
          if (
            (code === 'STREAM_ABORTED' || code === 'STREAM_IDLE_TIMEOUT' || code === 'TIMEOUT') &&
            get().currentTurnId
          ) {
            const turnId = get().currentTurnId as string
            try {
              await replayTurnEvents(turnId, lastSeq, handleEvent, myController.signal)
              if (!sawTerminal && get().isStreaming) {
                finalizeAssistantMessage(set, streamingId, {
                  citations: turnCitations,
                  libraryCitations: turnLibraryCitations,
                  retrievalStatus,
                  fallbackUsed,
                  artifact: artifactForThisTurn(),
                })
              }
              return true
            } catch {
              // fall through to error handling below
            }
          }

          set((state) => ({
            messages: dropOrSettleReply(state.messages, (m) => m.id === streamingId),
            isStreaming: false,
            streamingContent: '',
            currentTurnId: null,
            error: error instanceof Error ? error.message : 'Failed to send message',
            ...settleStreamingArtifact(state),
          }))
        } finally {
          // Identity guard: only clear the module controller if it is still OURS.
          // A newer send may have replaced it; nulling then would strand that
          // send's cancel/replay wiring.
          if (streamAbortController === myController) streamAbortController = null
        }

        return true
      },

      cancelStream: () => {
        streamAbortController?.abort()
        streamAbortController = null
        const { currentTurnId, sessionId } = get()
        if (currentTurnId || sessionId) {
          void cancelAgentTurn({
            ...(currentTurnId ? { turnId: currentTurnId } : {}),
            ...(sessionId ? { sessionId } : {}),
          }).catch(() => { /* best-effort */ })
        }
        set((state) => ({
          isStreaming: false,
          streamingContent: '',
          currentTurnId: null,
          messages: dropOrSettleReply(state.messages, (m) => m.isStreaming === true),
          ...settleStreamingArtifact(state),
        }))
      },

      clearError: () => {
        set({ error: null })
      },

      undoMemorySaved: (messageId, memoryId) =>
        undoMemorySavedLine(
          () => get().messages,
          (update) => set((state) => ({ messages: update(state.messages) })),
          messageId,
          memoryId
        ),
    }),
    { name: 'assistant-store' }
  )
)

// ===================
// §2.7 activity-phase feed (store-level subscription)
// ===================

/**
 * Fold every raw `lastStreamEvent` through the activity-phase reducer.
 *
 * This MUST be a store-level subscription (not a React render selector): the
 * callback fires synchronously on every `set`, so it observes every event in
 * order — React batching would skip intermediate events. The identity guard on
 * `lastStreamEvent` makes the re-entrant `setState` below terminate (updating
 * `activityPhases` doesn't change `lastStreamEvent`).
 */
useAssistantStore.subscribe((state, prev) => {
  if (state.lastStreamEvent === prev.lastStreamEvent) return
  const event = state.lastStreamEvent
  if (!event) return
  const next = reduceActivityPhases(state.activityPhases, event)
  if (next !== state.activityPhases) {
    useAssistantStore.setState({ activityPhases: next })
  }
})

// ===================
// Helper Functions
// ===================

type SetState = (
  partial:
    | Partial<AssistantChatState>
    | ((state: AssistantChatState) => Partial<AssistantChatState>)
) => void

/** Copy a session/threads/history response into the store state. */
function applySessionResponse(set: SetState, response: ChatLaneSessionResponse) {
  const activeThreadId = response.activeThreadId
  const relevant = activeThreadId
    ? response.turnHistory.filter((t) => !t.threadId || t.threadId === activeThreadId)
    : response.turnHistory
  const built = buildThreadPreviews(response.turnHistory)
  // Merge, don't replace: the backend returns only the active thread's history,
  // so a wholesale replace would drop previews stamped for other threads when
  // switching away and back. Merge keeps previously-stamped previews alive.
  set((state) => ({
    sessionId: response.sessionId,
    activeThreadId,
    threads: response.threads,
    threadPreviews: { ...state.threadPreviews, ...built },
    messages: hydrateMessages(relevant, response.sessionId),
    isLoading: false,
  }))
}

/** Map persisted turn history into user/assistant ChatMessages. */
function hydrateMessages(
  turnHistory: BidChatTurnHistoryEntry[],
  sessionId: string
): ChatMessage[] {
  const messages: ChatMessage[] = []
  for (const turn of turnHistory) {
    messages.push({
      id: `${turn.turnId}-user`,
      sessionId,
      role: 'user',
      content: turn.userMessage,
      createdAt: turn.createdAt,
      ...(turn.attachments && turn.attachments.length > 0
        ? { attachments: turn.attachments }
        : {}),
    })
    if (turn.assistantResponse != null) {
      const sources = mapPersistedCitations(turn.assistantMetadata?.citations)
      const librarySources = mapPersistedCitations(turn.assistantMetadata?.libraryCitations)
      // Persisted honest-status: map retrievalStatus/fallbackUsed → the same
      // retrievalStatus + retrievalDetail the live stream sets, so the honest
      // line survives a reload. Gating mirrors finalizeAssistantMessage.
      const persistedStatus = turn.assistantMetadata?.retrievalStatus
      const retrievalDetail = deriveRetrievalDetail(
        persistedStatus,
        turn.assistantMetadata?.fallbackUsed ?? false
      )
      messages.push({
        id: `${turn.turnId}-assistant`,
        sessionId,
        role: 'assistant',
        content: turn.assistantResponse,
        createdAt: turn.completedAt ?? turn.createdAt,
        ...(sources.length > 0 ? { sources } : {}),
        ...(librarySources.length > 0 ? { librarySources } : {}),
        ...(persistedStatus && persistedStatus !== 'ok' ? { retrievalStatus: persistedStatus } : {}),
        ...(retrievalDetail ? { retrievalDetail } : {}),
        ...(turn.assistantMetadata?.artifact
          ? { artifact: { turnId: turn.turnId, ...turn.assistantMetadata.artifact } }
          : {}),
      })
    }
  }
  return messages
}

type PersistedCitation = NonNullable<
  NonNullable<BidChatTurnHistoryEntry['assistantMetadata']>['citations']
>[number]

function mapPersistedCitations(raw: PersistedCitation[] | undefined): CitationSource[] {
  if (!Array.isArray(raw)) return []
  return raw.map((c): CitationSource => {
    const src: CitationSource = {
      documentId: c.documentId,
      documentName: c.documentName,
      pageNumber: c.pageNumber ?? null,
    }
    if (typeof c.snippet === 'string') src.snippet = c.snippet
    if (typeof c.quote === 'string') src.quote = c.quote
    if (c.highlightData) src.highlightData = c.highlightData as SourceHighlightData
    return src
  })
}

/** First user message per thread, for the sidebar labels. */
function buildThreadPreviews(turnHistory: BidChatTurnHistoryEntry[]): Record<string, string> {
  const previews: Record<string, string> = {}
  for (const turn of turnHistory) {
    const threadId = turn.threadId
    if (!threadId || previews[threadId]) continue
    previews[threadId] = previewFromContent(turn.userMessage)
  }
  return previews
}

/**
 * First-line thread preview: collapse whitespace, truncate at ~50 chars on a
 * word boundary (falling back to a hard cut for a single long token), append an
 * ellipsis. Exported for unit testing.
 */
export function previewFromContent(content: string): string {
  const cleaned = content.trim().replace(/\s+/g, ' ')
  const maxLength = 50
  if (cleaned.length <= maxLength) return cleaned
  const truncated = cleaned.slice(0, maxLength)
  const lastSpace = truncated.lastIndexOf(' ')
  return (lastSpace > 20 ? truncated.slice(0, lastSpace) : truncated) + '…'
}

/**
 * Accumulate citations from an `evidence_search_completed` payload. Later
 * evidence passes REPLACE earlier ones (last-wins per family — matches the
 * panel/tender store and the backend `buildTurnCitationsFromEvents` collapse, so
 * live-stream and rehydrated citations resolve label N to the same source).
 */
function mergeCitations(target: CitationSource[], raw: unknown) {
  if (!Array.isArray(raw) || raw.length === 0) return
  target.length = 0
  for (const entry of raw) {
    const c = entry as Record<string, unknown>
    const src: CitationSource = {
      documentId: c['documentId'] as string,
      documentName: c['documentName'] as string,
      pageNumber: (c['pageNumber'] as number | null) ?? null,
    }
    if (typeof c['snippet'] === 'string') src.snippet = c['snippet']
    if (typeof c['quote'] === 'string') src.quote = c['quote']
    if (c['highlightData']) src.highlightData = c['highlightData'] as SourceHighlightData
    target.push(src)
  }
}

/**
 * Positional quote merge (ported from the editor panel — NOT imported):
 * label "N" → citations[N-1], "LN" → libraryCitations[N-1]. Unparseable or
 * out-of-range labels are ignored; no other citation field is clobbered.
 */
function mergeQuotes(
  citations: CitationSource[],
  libraryCitations: LibraryCitationSource[],
  raw: unknown
) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return
  for (const [label, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value !== 'string' || value.length === 0) continue
    const match = /^(L?)(\d+)$/.exec(label)
    if (!match) continue
    const isLibrary = match[1] === 'L'
    const idx = Number.parseInt(match[2]!, 10)
    if (!Number.isInteger(idx) || idx < 1) continue
    const citation = (isLibrary ? libraryCitations : citations)[idx - 1]
    if (citation) citation.quote = value
  }
}

/**
 * Copies of a turn's citation lists for the artifact panel, omitting an empty
 * family (same shape as the assistant message). Copied, not shared: the live
 * accumulators are mutated in place by later quote merges.
 */
function artifactSourcesFrom(
  citations: CitationSource[],
  libraryCitations: LibraryCitationSource[]
): Pick<AssistantArtifact, 'sources' | 'librarySources'> {
  return {
    ...(citations.length > 0 ? { sources: citations.map((c) => ({ ...c })) } : {}),
    ...(libraryCitations.length > 0
      ? { librarySources: libraryCitations.map((c) => ({ ...c })) }
      : {}),
  }
}

/**
 * Settle an artifact left mid-flight by an errored or cancelled turn.
 *
 * No further `artifact_chunk`/`artifact_completed` will arrive, so a
 * `'streaming'` document would spin forever. Keep the partial text — it is
 * still useful to the user — and mark it `truncated` so the panel shows its
 * cut-short notice. An already-complete document is left untouched.
 */
function settleStreamingArtifact(
  state: AssistantChatState
): { artifact?: AssistantArtifact } {
  const artifact = state.artifact
  if (!artifact || artifact.status !== 'streaming') return {}
  return { artifact: { ...artifact, status: 'complete', truncated: true } }
}

/** Finalize the streaming assistant message: attach citations + honest status. */
function finalizeAssistantMessage(
  set: SetState,
  streamingId: string,
  turn: {
    citations: CitationSource[]
    libraryCitations: LibraryCitationSource[]
    retrievalStatus: 'ok' | 'partial' | 'empty' | 'weak' | undefined
    fallbackUsed: boolean
    artifact: ChatMessage['artifact']
  }
) {
  const sources = turn.citations.length > 0 ? turn.citations.map((c) => ({ ...c })) : undefined
  const librarySources = turn.libraryCitations.length > 0
    ? turn.libraryCitations.map((c) => ({ ...c }))
    : undefined
  const retrievalDetail = deriveRetrievalDetail(turn.retrievalStatus, turn.fallbackUsed)

  set((state) => ({
    // The panel's document belongs to this turn: give it the final citation
    // lists too (quotes can land after artifact_completed).
    ...(turn.artifact && state.artifact?.turnId === turn.artifact.turnId
      ? {
          artifact: {
            ...state.artifact,
            groundedSourceCount: turn.citations.length + turn.libraryCitations.length,
            ...artifactSourcesFrom(turn.citations, turn.libraryCitations),
          },
        }
      : {}),
    messages: state.messages.map((m) =>
      m.id === streamingId
        ? {
            ...m,
            isStreaming: false,
            ...(sources ? { sources } : {}),
            ...(librarySources ? { librarySources } : {}),
            ...(turn.retrievalStatus && turn.retrievalStatus !== 'ok'
              ? { retrievalStatus: turn.retrievalStatus }
              : {}),
            ...(retrievalDetail ? { retrievalDetail } : {}),
            ...(turn.artifact ? { artifact: turn.artifact } : {}),
          }
        : m
    ),
    isStreaming: false,
    streamingContent: '',
    currentTurnId: null,
  }))
}

function errorCode(error: unknown): string | null {
  if (error instanceof Error && 'code' in error && typeof (error as { code?: unknown }).code === 'string') {
    return (error as { code: string }).code
  }
  return null
}
